import invariant from 'tiny-invariant';

var b64Decode = /*#__PURE__*/function () {
  if (isNodejs()) {
    return function (s) {
      return Buffer.from(s, 'base64').toString('binary');
    };
  }

  return window.atob;
}();
var generateID = /*#__PURE__*/function () {
  if (isNodejs()) {
    var _crypto = /*#__PURE__*/require('crypto');

    return function () {
      return _crypto.randomBytes(16).toString('base64');
    };
  } else {
    return function () {
      var arr = new Uint8Array(16);
      crypto.getRandomValues(arr); //    note this only works for short sequences because it is recursive

      return btoa(String.fromCharCode.apply(null, Array.from(arr.values())));
    };
  }
}();

function isNodejs() {
  return  process && process.versions && process.versions.node;
}

function isOlderVS(older, newer) {
  if (!older) return true;
  if (!newer) return false;
  older = b64Decode(older);
  newer = b64Decode(newer);
  var maxLength = Math.max(older.length, newer.length);
  older = leftPadZero(older, maxLength);
  newer = leftPadZero(newer, maxLength);
  return older < newer;
}

function leftPadZero(input, desiredLen) {
  if (input.length < desiredLen) {
    return '\x00'.repeat(desiredLen - input.length) + input;
  }

  return input;
}

function escape(value) {
  return JSON.stringify(value);
}
function unescape(value) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
}

/*
    Map consistency model
    `mput`: Last writer (highest versionstamp) wins. On a local put, we leave
    the existing versionstamp so that newer writes from others will overwrite
    it. The eventual versionstamped confirmation message of the local put
    will establish what versionstamp it was assigned and must be greater than
    the existing versionstamp. Note that acks of a value that was set locally
    update the versionstamp but leave the value so that we don't revert to an
    older value in the case of multiple updates happening locally before an
    ack is received.
    
    `mdel`: Last deleter wins. Instead of just deleting the key, we store the
    versionstamp of the delete (essentially a tombstone). This way, we can
    compare the versionstamp of future `mput`s to know whether they should
    un-delete the value or not. Tombstones are cleaned up after a short
    amount of time as they only need to outlive messages in-flight from Room
    Service.
*/
/**
 * Populates the store with the values given from a bootstrap'd
 * checkpoint, typically gotten from Room Service's API.
 * @param store
 * @param rawCheckpoint
 */

function importFromRawCheckpoint(store, rawCheckpoint, mapID) {
  store.kv.clear();
  store.tombstoneCleanup = [];

  if (!rawCheckpoint.maps[mapID]) {
    return; // no import
  }

  for (var k in rawCheckpoint.maps[mapID]) {
    var val = rawCheckpoint.maps[mapID][k];

    if (typeof val === 'string') {
      store.kv.set(k, {
        value: unescape(val),
        versionstamp: rawCheckpoint.vs,
        local: false
      });
    }
  }
}
/**
 * Validates an incoming command. Throws an error
 * if invalid.
 * @param meta
 * @param cmd A room service command
 */


function validateCommand(meta, cmd) {
  if (cmd.length < 3) {
    throw new Error('Unexpected command: ' + cmd);
  }

  var docID = cmd[1];
  var id = cmd[2];

  if (docID !== meta.docID || id !== meta.mapID) {
    throw new Error('Command unexpectedly routed to the wrong client');
  }
}

var MALFORMED = 'Malformed command ';
/**
 * Applies a command to the in-memory store.
 * @param store
 * @param cmd A room service command
 */

function applyCommand(store, cmd, versionstamp, ack) {
  tombstoneCleanup(store);
  var keyword = cmd[0];

  switch (keyword) {
    case 'mput':
      return applyPut(store, cmd, versionstamp, ack);

    case 'mdel':
      return applyDelete(store, cmd, versionstamp, ack);
    // These are technically "valid" in some places
    // but can be ignored here

    case 'mcreate':
      break;

    default:
      throw new Error('Unexpected command keyword: ' + keyword);
  }
}

function applyPut(store, cmd, versionstamp, ack) {
  var _existing$local;

  if (cmd.length !== 5) {
    console.error(MALFORMED, cmd);
    return;
  }

  var putKey = cmd[3];
  var putVal = cmd[4];
  var existing = store.kv.get(putKey);
  var existingPutVersionstamp = existing == null ? void 0 : existing.versionstamp;

  if (existingPutVersionstamp && isOlderVS(versionstamp, existingPutVersionstamp)) {
    return;
  }

  var isPutLocalAck = ((_existing$local = existing == null ? void 0 : existing.local) != null ? _existing$local : false) && ack;

  if (isPutLocalAck) {
    existing.versionstamp = versionstamp;
    return;
  }

  store.kv.set(putKey, {
    value: unescape(putVal),
    versionstamp: versionstamp,
    local: false
  });
}

function applyDelete(store, cmd, versionstamp, ack) {
  var _existingObj$local;

  if (cmd.length !== 4) {
    console.error(MALFORMED, cmd);
    return;
  }

  var delKey = cmd[3];
  var existingObj = store.kv.get(delKey);
  var existingDelVersionstamp = existingObj == null ? void 0 : existingObj.versionstamp;

  if (existingDelVersionstamp && isOlderVS(versionstamp, existingDelVersionstamp)) {
    return;
  }

  var now = performance.now();
  var isLocalAck = ((_existingObj$local = existingObj == null ? void 0 : existingObj.local) != null ? _existingObj$local : false) && ack;

  if (isLocalAck) {
    existingObj.versionstamp = versionstamp;
    return;
  }

  store.kv.set(delKey, {
    value: undefined,
    versionstamp: versionstamp,
    deletedAt: now,
    local: false
  });
  store.tombstoneCleanup.push({
    key: delKey,
    deletedAt: now
  });
}
/**
 * Runs .set() on the store, returns the command for external use.
 * @param store
 * @param meta
 * @param key
 * @param value
 * @return {Array} ['mput', meta.docID, meta.mapID, key, escapedValue]
 */


function runSet(store, meta, key, value) {
  var escaped = escape(value); // Local

  if (store.kv.has(key)) {
    //  inherit old versionstamp
    store.kv.get(key).value = value;
    store.kv.get(key).local = true;
  } else {
    store.kv.set(key, {
      value: value,
      versionstamp: undefined,
      local: true
    });
  } // Remote


  return ['mput', meta.docID, meta.mapID, key, escaped];
}
/**
 * Runs .delete() on the store, returns the command for external use.
 * @param store
 * @param meta
 * @param key
 * @return {Array} ['mdel', meta.docID, meta.mapID, key]
 */


function runDelete(store, meta, key) {
  var _store$kv$get;

  tombstoneCleanup(store);
  var now = performance.now();
  var existingVS = (_store$kv$get = store.kv.get(key)) == null ? void 0 : _store$kv$get.versionstamp;
  store.kv.set(key, {
    value: undefined,
    versionstamp: existingVS,
    deletedAt: now,
    local: true
  });
  store.tombstoneCleanup.push({
    key: key,
    deletedAt: now
  }); // remote

  return ['mdel', meta.docID, meta.mapID, key];
}
/**
 * Returns the command to create a new map
 * @param docID
 * @param mapID
 */


function newMap(docID, mapID) {
  return {
    cmd: ['mcreate', docID, mapID],
    meta: {
      docID: docID,
      mapID: mapID
    },
    store: {
      kv: new Map(),
      tombstoneCleanup: []
    }
  };
}

var TOMBSTONE_TTL_MS = /*#__PURE__*/function () {
  if (process.env.NODE_ENV === 'test') {
    return 0;
  }

  return 10 * 1000;
}();

function tombstoneCleanup(store) {
  var now = performance.now();

  while (store.tombstoneCleanup.length > 0 && now >= store.tombstoneCleanup[0].deletedAt + TOMBSTONE_TTL_MS) {
    var _entry$deletedAt;

    var key = store.tombstoneCleanup.shift().key;
    var entry = store.kv.get(key);

    if (!entry) {
      continue;
    }

    if (entry.value === undefined && now >= ((_entry$deletedAt = entry.deletedAt) != null ? _entry$deletedAt : 0) + TOMBSTONE_TTL_MS) {
      store.kv["delete"](key);
    }
  }
}

var MapInterpreter = {
  validateCommand: validateCommand,
  applyCommand: applyCommand,
  newMap: newMap,
  importFromRawCheckpoint: importFromRawCheckpoint,
  runSet: runSet,
  runDelete: runDelete
};

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it;

  if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;
      return function () {
        if (i >= o.length) return {
          done: true
        };
        return {
          done: false,
          value: o[i++]
        };
      };
    }

    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  it = o[Symbol.iterator]();
  return it.next.bind(it);
}

function parseItemID(checkpoint, id) {
  if (id === 'root') {
    return 'root';
  }

  var _id$split = id.split(':'),
      index = _id$split[0],
      a = _id$split[1];

  return index + ':' + checkpoint.actors[parseInt(a)];
}
/**
 * A Reverse Tree is one where the children point to the
 * parents, instead of the otherway around.
 *
 * We use a reverse tree because the "insert" operation
 * can be done in parallel.
 */

var ReverseTree = /*#__PURE__*/function () {
  function ReverseTree(session) {
    this.nodes = new Map();
    this.session = session;
  }

  var _proto = ReverseTree.prototype;

  _proto["import"] = function _import(checkpoint, listID) {
    !checkpoint ? process.env.NODE_ENV !== "production" ? invariant(false) : invariant(false) : void 0;
    this.nodes = new Map();
    var list = checkpoint.lists[listID];
    var afters = list.afters || [];
    var ids = list.ids || [];
    var values = list.values || []; // Rehydrate the cache

    for (var i = 0; i < afters.length; i++) {
      var node = {
        after: parseItemID(checkpoint, afters[i]),
        id: parseItemID(checkpoint, ids[i]),
        value: values[i],
        versionstamp: checkpoint.vs,
        local: false
      };
      this.nodes.set(node.id, node);
    }
  };

  _proto.get = function get(itemID) {
    var _this$nodes$get;

    return (_this$nodes$get = this.nodes.get(itemID)) == null ? void 0 : _this$nodes$get.value;
  };

  _proto.insert = function insert(params) {
    var after = params.after,
        value = params.value,
        externalNewID = params.externalNewID,
        localOrAck = params.localOrAck;
    var id = externalNewID;

    if (!id) {
      //  TODO: with list GC implemented, count should be incremented higher than
      //  any node id instead of based on current number of active nodes
      id = this.nodes.size + ":" + this.session;
    }

    if (this.nodes.has(id)) {
      var _this$nodes$get2;

      if (((_this$nodes$get2 = this.nodes.get(id)) == null ? void 0 : _this$nodes$get2.after) === undefined) {
        this.nodes.get(id).after = after;
      }

      this.put(id, value, localOrAck);
      return id;
    }

    var node = {
      after: after,
      value: value,
      id: id,
      local: localOrAck === 'local',
      versionstamp: typeof localOrAck === 'object' && localOrAck.versionstamp || undefined
    };
    this.nodes.set(id, node);
    return id;
  };

  _proto.put = function put(itemID, value, localOrAck) {
    var existing = this.nodes.get(itemID);

    if (!existing) {
      //  handle out of order lput/ldel and lins
      this.nodes.set(itemID, {
        after: undefined,
        value: value,
        versionstamp: typeof localOrAck === 'object' && localOrAck.versionstamp || undefined,
        id: itemID,
        local: localOrAck === 'local'
      });
      return;
    }

    if (localOrAck === 'local') {
      existing.value = value;
      existing.local = true;
      return;
    }

    var versionstamp = localOrAck.versionstamp;

    if (versionstamp && existing.versionstamp && isOlderVS(versionstamp, existing.versionstamp)) {
      return;
    }

    var isLocalAck = existing.local && localOrAck.ack;

    if (isLocalAck) ; else {
      existing.value = value;
      existing.local = false;
    }

    existing.versionstamp = versionstamp;
  };

  _proto.has = function has(itemID) {
    return this.nodes.has(itemID);
  };

  _proto["delete"] = function _delete(itemID, localOrAck) {
    this.put(itemID, {
      t: ''
    }, localOrAck);
  };

  _proto.toTree = function toTree() {
    var childrenById = new Map();
    var valueById = new Map();

    for (var _i = 0, _Array$from = Array.from(this.nodes.values()); _i < _Array$from.length; _i++) {
      var _childrenById$get;

      var node = _Array$from[_i];

      if (node.after === undefined) {
        continue;
      }

      if (!childrenById.has(node.after)) {
        childrenById.set(node.after, []);
      }

      (_childrenById$get = childrenById.get(node.after)) == null ? void 0 : _childrenById$get.push(node.id);
      valueById.set(node.id, node.value);
    }

    childrenById.forEach(function (children) {
      //  sort by logical timestamp descending so that latest inserts appear first
      children.sort(function (a, b) {
        var _a$split = a.split(':'),
            leftCount = _a$split[0],
            leftActor = _a$split[1];

        var _b$split = b.split(':'),
            rightCount = _b$split[0],
            rightActor = _b$split[1];

        if (leftCount === rightCount) {
          return leftActor.localeCompare(rightActor);
        }

        return parseInt(rightCount) - parseInt(leftCount);
      });
    });
    return {
      childrenById: childrenById,
      valueById: valueById
    };
  };

  _proto.lastID = function lastID() {
    if (this.nodes.size === 0) {
      return 'root';
    }

    var root = this.toTree(); // Search the right side of the tree

    function right(t, node) {
      var children = t.childrenById.get(node);

      if (!children || children.length === 0) {
        return node;
      }

      return right(t, children[children.length - 1]);
    }

    return right(root, 'root');
  };

  _proto.preOrderTraverse = function preOrderTraverse() {
    // -- Convert the log into a regular tree
    var tree = this.toTree();
    var seenNodes = new Set(); // -- Do a depth-first traversal to get the result

    function preOrder(t, node) {
      if (seenNodes.has(node)) {
        console.warn('RoomService list cycle detected. Consider updating @roomservice/browser.');
        return [];
      }

      seenNodes.add(node);
      var result = [];
      var value = t.valueById.get(node);

      if (value) {
        if (typeof value === 'string') {
          result.push({
            value: value,
            id: node
          });
        } else if ('t' in value && value.t === '') ; else {
          throw new Error('Unimplemented');
        }
      }

      var children = t.childrenById.get(node);

      if (!children || children.length === 0) {
        return result;
      }

      for (var _iterator = _createForOfIteratorHelperLoose(children), _step; !(_step = _iterator()).done;) {
        var child = _step.value;
        result = result.concat(preOrder(t, child));
      }

      return result;
    }

    return preOrder(tree, 'root');
  };

  _proto.toArray = function toArray() {
    return this.preOrderTraverse().map(function (idValue) {
      return idValue.value;
    });
  };

  _createClass(ReverseTree, [{
    key: "length",
    get: function get() {
      return Object.keys(this.nodes).length;
    }
  }]);

  return ReverseTree;
}();

/*

    List consistency model
    `lput`, `ldel`: Values of nodes are treated like a map (last write wins), including a
    possible tombstone value for deleted nodes.  For now tombstones are not cleaned up.
    
    `lins`: Every node id is prefixed with this client's random identifier
    (could be thought of as a session identifier). Note that this is not the
    actorID as there could be multiple active sessions for the same actor.
    Using the session identifier makes this client's inserts independent from
    all others, but the versionstamp is still important for relative ordering
    of lput and lins commands.
*/

function newList(docID, listID) {
  return {
    store: {
      itemIDs: [],
      rt: new ReverseTree(generateID())
    },
    meta: {
      docID: docID,
      listID: listID
    },
    cmd: ['mcreate', docID, listID]
  };
}
/**
 * Populates the store with the values given from a bootstrap'd
 * checkpoint, typically from Room Service's API.
 * @param store
 * @param rawCheckpoint
 */


function importFromRawCheckpoint$1(store, _actor, rawCheckpoint, listID) {
  store.itemIDs = [];

  if (!rawCheckpoint.lists[listID]) {
    return; // no import
  }

  store.rt["import"](rawCheckpoint, listID);
  var list = rawCheckpoint.lists[listID];
  var ids = list.ids || [];

  for (var i = 0; i < ids.length; i++) {
    var val = rawCheckpoint.lists[listID].values[i];

    if (typeof val === 'object' && val['t'] === '') {
      continue; // skip tombstones
    }

    store.itemIDs.push(parseItemID(rawCheckpoint, ids[i]));
  }
}
/**
 * Validates an incoming command. Throws an error
 * if invalid.
 * @param meta
 * @param cmd A room service command
 */


function validateCommand$1(meta, cmd) {
  if (cmd.length < 3) {
    throw new Error('Unexpected command: ' + cmd);
  }

  var docID = cmd[1];
  var id = cmd[2];

  if (docID !== meta.docID || id !== meta.listID) {
    throw new Error('Command unexpectedly routed to the wrong client');
  }
}
/**
 * Applies a command to the in-memory store.
 * @param store
 * @param cmd A room service command
 */


function applyCommand$1(store, cmd, versionstamp, ack) {
  var keyword = cmd[0];

  switch (keyword) {
    case 'lins':
      var insAfter = cmd[3];
      var insItemID = cmd[4];
      var insValue = cmd[5];
      store.itemIDs.splice(store.itemIDs.findIndex(function (f) {
        return f === insAfter;
      }) + 1, 0, insItemID);
      store.rt.insert({
        after: insAfter,
        value: insValue,
        externalNewID: insItemID,
        localOrAck: {
          ack: ack,
          versionstamp: versionstamp
        }
      });
      break;

    case 'lput':
      var putItemID = cmd[3];
      var putVal = cmd[4];
      store.rt.put(putItemID, putVal, {
        ack: ack,
        versionstamp: versionstamp
      });
      break;

    case 'ldel':
      var delItemID = cmd[3];
      store.rt["delete"](delItemID, {
        ack: ack,
        versionstamp: versionstamp
      });
      store.itemIDs.splice(store.itemIDs.findIndex(function (f) {
        return f === delItemID;
      }), 1);
      break;
    // These are technically "valid" in some places
    // but can be ignored here

    case 'lcreate':
      break;

    default:
      throw new Error('Unexpected command keyword: ' + keyword);
  }
}

function get(store, index) {
  var itemID = store.itemIDs[index];
  if (!itemID) return undefined;
  var val = store.rt.get(itemID);
  if (!val) return undefined;

  if (typeof val === 'object') {
    if (val.t === '') {
      return undefined;
    }

    throw new Error('Unimplemented references');
  }

  return unescape(val);
}

function runSet$1(store, meta, index, val) {
  var itemID = store.itemIDs[index];

  if (!itemID) {
    throw new Error("Index '" + index + "' doesn't already exist. Try .push() or .insertAfter() instead.");
  }

  var escaped = escape(val);
  store.rt.put(itemID, escaped, 'local'); // Remote

  return ['lput', meta.docID, meta.listID, itemID, escaped];
}

function runDelete$1(store, meta, index) {
  if (store.itemIDs.length === 0) {
    return false;
  }

  var itemID = store.itemIDs[index];

  if (!itemID) {
    console.warn('Unknown index: ', index, store.itemIDs);
    return false;
  }

  store.rt["delete"](itemID, 'local');
  store.itemIDs.splice(index, 1); // Remote

  return ['ldel', meta.docID, meta.listID, itemID];
}

function runInsertAt(store, meta, index, val) {
  if (index < 0) {
    throw new Error('Negative indices unsupported');
  }

  var afterID;

  if (index === 0) {
    afterID = 'root';
  } else {
    afterID = store.itemIDs[index - 1];
  }

  if (!afterID) {
    throw new RangeError("List '" + meta.listID + "' has no index: '" + index + "'");
  }

  var escaped = escape(val);
  var itemID = store.rt.insert({
    after: afterID,
    value: escaped,
    externalNewID: undefined,
    localOrAck: 'local'
  });
  store.itemIDs.splice(index, 0, itemID);
  return ['lins', meta.docID, meta.listID, afterID, itemID, escaped];
}

function runInsertAfter(store, meta, index, val) {
  return runInsertAt(store, meta, index + 1, val);
}

function runPushOne(store, meta, val) {
  var lastID = store.rt.lastID();
  var escaped = escape(val); // Local

  var itemID = store.rt.insert({
    after: lastID,
    value: escaped,
    externalNewID: undefined,
    localOrAck: 'local'
  });
  store.itemIDs.push(itemID); // Remote

  return ['lins', meta.docID, meta.listID, lastID, itemID, escaped];
}

function runPush(store, meta) {
  var cmds = [];

  for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    args[_key - 2] = arguments[_key];
  }

  for (var _i = 0, _args = args; _i < _args.length; _i++) {
    var arg = _args[_i];
    cmds.push(runPushOne(store, meta, arg));
  }

  return cmds;
}

function map(store, fn) {
  return store.rt.preOrderTraverse().map(function (idValue, i) {
    return fn(unescape(idValue.value), i, idValue.id);
  });
}

function toArray(store) {
  return store.rt.toArray().map(function (m) {
    return unescape(m);
  });
}

var ListInterpreter = {
  importFromRawCheckpoint: importFromRawCheckpoint$1,
  validateCommand: validateCommand$1,
  applyCommand: applyCommand$1,
  get: get,
  map: map,
  toArray: toArray,
  newList: newList,
  runSet: runSet$1,
  runDelete: runDelete$1,
  runInsertAt: runInsertAt,
  runInsertAfter: runInsertAfter,
  runPush: runPush
};

export { ListInterpreter, MapInterpreter, isOlderVS };
//# sourceMappingURL=core.esm.js.map
